import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
	RecipeBreadcrumbService,
	GridComponent,
	SortHeaderComponent,
	ResultsHeaderComponent,
	BreadcrumbComponent,
} from '@woolworthsnz/recipe';
import { InfiniteScrollingService, SearchMediatorService } from '@woolworthsnz/search';
import {
	AppSettingsService,
	DatalayerService,
	ModalOverlayService,
	WithPageMeta,
	WappleLoadingComponent,
	EmbeddedVisibilityDirective,
	LoadMoreComponent,
} from '@woolworthsnz/styleguide';
import { RecipeSearchRequest } from '@woolworthsnz/trader-api';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { RecipePageType, RecipesStoreService } from '../../services/recipes-store.service';
import { RecipesResultsBasePage } from '../recipes-results-base/recipes-results-base.page';
import { NgIf, AsyncPipe } from '@angular/common';

@WithPageMeta({ pageTitle: 'recipeSearch' })
@Component({
	template: `
		<ng-container *ngIf="state$ | async; let state">
			<cdx-wapple-loading *ngIf="state.loading; else content"></cdx-wapple-loading>
			<ng-template #content>
				<header>
					<recipe-breadcrumb *cdxEmbedded="'hide'"></recipe-breadcrumb>
					<recipe-results-header
						*ngIf="isResultHeaderVisible$ | async"
						[title]="state.searchQuery"
						[totalRecipes]="state.totalRecipes"
						[searchMode]="true"
					></recipe-results-header>
					<recipe-sort-header
						*ngIf="state.totalRecipes > 0"
						[sortOptions]="state.sortOptions"
						[currentSortOption]="state.currentSortOption"
						(sortChange)="sortChange($event)"
					></recipe-sort-header>
				</header>
				<div class="recipeItems">
					<recipe-grid
						[savedRecipeIds]="state.savedRecipes"
						[recipeStamps]="mapToStamps(state.visibleRecipes)"
						(saveRecipe)="handleSave($event)"
						(columnCountChange)="handleColumnCountChange($event)"
					></recipe-grid>
				</div>
				<footer>
					<cdx-load-more
						[currentlyShowing]="state.visibleRecipes.length"
						[totalItems]="state.totalRecipes"
						[loading]="state.newPageLoading"
						(loadMore)="loadMore()"
					>
						<span i18n="@@recipes-loadMoreButton">Load more deliciousness</span>
					</cdx-load-more>
				</footer>
			</ng-template>
			<div #scrollAnchor></div>
		</ng-container>
	`,
	styleUrls: ['../recipes-results-base/recipes-results-base.page.scss'],
	standalone: true,
	imports: [
		NgIf,
		WappleLoadingComponent,
		EmbeddedVisibilityDirective,
		BreadcrumbComponent,
		ResultsHeaderComponent,
		SortHeaderComponent,
		GridComponent,
		LoadMoreComponent,
		AsyncPipe,
	],
})
export class RecipesSearchPage extends RecipesResultsBasePage implements OnInit, OnDestroy {
	@ViewChild('scrollAnchor') scrollAnchor: ElementRef<HTMLElement>;
	private destroy$: Subject<boolean> = new Subject();

	constructor(
		protected recipesStore: RecipesStoreService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected modalOverlayService: ModalOverlayService,
		protected searchMediatorService: SearchMediatorService,
		protected infiniteScrollingService: InfiniteScrollingService,
		protected datalayerService: DatalayerService,
		protected appSettingsService: AppSettingsService,
		protected recipeBreadcrumbService: RecipeBreadcrumbService
	) {
		super(
			recipesStore,
			route,
			router,
			modalOverlayService,
			searchMediatorService,
			infiniteScrollingService,
			datalayerService,
			RecipePageType.search,
			recipeBreadcrumbService
		);
	}

	ngOnInit(): void {
		this.initialize();

		this.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
			this.recipeBreadcrumbService.setBreadcrumbs([
				{ text: 'Search Results', queryParams: null },
				{ text: state.searchQuery ?? '', queryParams: null },
			]);
		});

		this.route.queryParamMap
			.pipe(
				takeUntil(this.destroy$),
				map((queryParams) => ({
					query: queryParams.get('q') || '',
					target: (queryParams.get('target') || undefined) as RecipeSearchRequest.TargetEnum | undefined,
				})),
				distinctUntilChanged((x, y) => x.query === y.query && x.target === y.target),
				mergeMap(({ query, target }) =>
					this.recipesStore.searchRecipes(query, this.sortOption, target).pipe(this.takeUntilRouteChange())
				),
				tap(() => this.setupScroller(this.scrollAnchor))
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
		this.disconnect();
	}

	get isResultHeaderVisible$(): Observable<boolean> {
		if (!this.appSettingsService.getSetting('isEmbeddedInApp')) {
			return of(true);
		}

		// Embedded app will only show header when there are no results
		return this.recipesStore.state$.pipe(
			takeUntil(this.destroy$),
			map((state) => state.totalRecipes === 0)
		);
	}
}
